// MUST be the first import: it snapshots the raw URL fragment at module-eval,
// before './router' is evaluated. Creating the hash-history rewrites a non-route
// fragment like `#code=...` to `#/code=...`, which would corrupt the MSAL
// response and defeat detection. See @/services/msalRedirectGuard.
import { handleMsalRedirectLanding } from '@/services/msalRedirectGuard'

import { createApp } from 'vue'
import App from './App.vue'
// Validates the required runtime config (api/auth endpoints) and fails fast on a
// misconfigured deploy, before any module reads it. See @/config/runtime.
import '@/config/runtime'
import { log } from '@/logging'
import { createPinia } from 'pinia'
import { migrateFromLocalStorage } from '@/services/authStorage'

import router from './router'
import store from './store'
import ElementPlus from 'element-plus'
import i18n from '@/core/plugins/i18n'

import { initApexCharts } from '@/core/plugins/apexcharts'
import { initInlineSvg } from '@/core/plugins/inline-svg'
import { initVeeValidate } from '@/core/plugins/vee-validate'

import '@/core/plugins/prismjs'

function bootstrapApp(): void {
  // Remove stale auth tokens from localStorage (pre-sessionStorage migration).
  // Forces re-login on first load after this deploy — intentional security reset.
  migrateFromLocalStorage()

  const app = createApp(App)

  app.use(createPinia())
  app.use(store)
  app.use(router)
  app.use(ElementPlus)

  initApexCharts(app)
  initInlineSvg(app)
  initVeeValidate()

  app.use(i18n)

  app.config.errorHandler = (err, _instance, info) => {
    log.error('Unhandled Vue error', err, { lifecycleHook: info })
  }
  window.addEventListener('unhandledrejection', (event) => {
    log.error('Unhandled promise rejection', event.reason)
  })

  app.mount('#app')
}

// MSAL redirect guard — must precede app bootstrap. On the MSAL popup landing
// (`<redirectUri>/#code=...`), booting the hash-router would navigate to `#/404`
// and destroy the auth code before the opener reads it (→ "404 in popup", failed
// login). handleMsalRedirectLanding restores the raw fragment and tells us to skip
// bootstrap; the opener's MSAL then reads the code and closes the popup.
if (handleMsalRedirectLanding()) {
  log.info('MSAL redirect landing — skipped app bootstrap')
} else {
  bootstrapApp()
}
